/**
 * Flow Queuing System — the enroll-queue provider's per-item "Reset to base
 * copy", extracted verbatim from `EnrollQueueContext.tsx`.
 *
 * terra-ohe.7 (F1/DBOS durable-send migration U6) — this file used to ALSO
 * hold the client-side send-time materialization function (staging staged
 * base copy onto each item's own `subject_draft`/`body_draft`/`sender` via a
 * pre-POST PATCH) plus its three private helpers
 * (`repOwnedSender`/`repOwnedCopy`/`latestWrittenPatch`) and the
 * `StagedAutosaveEntry`/`StagedAutosaveEntryMap` structural types they read.
 * All of that is DELETED by this bead: U2 moved trio resolution server-side
 * (the claim itself now resolves `base_copy`/`base_sender`/`sender_touched`
 * against each row it admits), so a pre-POST PATCH plus its compensating
 * rollback closure (a `TrackedItemPatch.rollback` flag, and a returned undo
 * function) has no reason to exist — exactly the "client-side
 * 'materialization'" anti-pattern the epic's coding standards call out. The
 * wire-shape builder that replaces it, `buildStagedSendSnapshot`, is a small
 * PURE function in `state/staged-play-config.ts` — it needs no hook, no
 * autosave-entry access, and does not belong in this file.
 *
 * `resetItemToBase` is UNCHANGED and stays here: it is a real per-item write
 * (PATCH .../items/:id back to base-following) triggered by the rep's own
 * "Reset" action, not a send-path resolution concern — see
 * `EnrollQueueContextValue.resetItemToBase` for its full contract.
 *
 * Everything that wraps `patchMutation` directly (`patchItem`,
 * `flushAutosave`, `discardAutosave`) stays declared in `useItemAutosave.ts`
 * (moved out of the context file by this same bead) and is handed to this
 * hook by reference — this hook never creates, reorders, or reaches into one
 * of those directly.
 */
import type { QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { EnrollmentItem } from '@shared/flow/enrollment';
import { clearSenderTouched } from './staged-play-config';
import { requestItemDraftReset } from './item-draft-reset';
import { QUEUE_KEY } from '../queue-query-keys';
import type { EnrollQueueContextValue } from '../enroll-queue-types';

/** Same-play comparison used by the re-validation point below. */
const samePlay = (
  item: EnrollmentItem | undefined,
  play: { play_type_id: number | null; play_instance_ref: string | null } | undefined,
): boolean =>
  item !== undefined
  && play !== undefined
  && item.play_type_id === play.play_type_id
  && item.play_instance_ref === play.play_instance_ref;

export interface UseStagedMaterializationDeps {
  stagedIdentity: string | null;
  runItemOp: EnrollQueueContextValue['runItemOp'];
  cachedItem: (itemId: number) => EnrollmentItem | undefined;
  beginItemWriteBarrier: (itemId: number) => void;
  endItemWriteBarrier: (itemId: number) => void;
  discardAutosave: EnrollQueueContextValue['discardAutosave'];
  patchItem: EnrollQueueContextValue['patchItem'];
  queryClient: QueryClient;
}

export interface StagedMaterialization {
  resetItemToBase: EnrollQueueContextValue['resetItemToBase'];
}

export function useStagedMaterialization({
  stagedIdentity,
  runItemOp,
  cachedItem,
  beginItemWriteBarrier,
  endItemWriteBarrier,
  discardAutosave,
  patchItem,
  queryClient,
}: UseStagedMaterializationDeps): StagedMaterialization {
  /**
   * D12 Codex round-2 fixes #2 and #3 — "Reset to base copy", as ONE
   * barriered operation. See `EnrollQueueContextValue.resetItemToBase` for the
   * step-by-step contract and the two races it closes; the mechanics that
   * matter here:
   *
   * - The barrier is claimed SYNCHRONOUSLY, before the first await, and
   *   released in a `finally`. That is what makes it a barrier rather than
   *   another drain: `autosavePatch` refuses to create an entry and
   *   `dispatchAutosave` refuses to fire a pre-armed timer for the whole
   *   window, so an edit made anywhere inside it cannot outlive the reset.
   * - The `QUEUE_KEY` invalidation is AWAITED (the same sequencing
   *   `assignPlay`/`addMutation` already document, and for the same reason):
   *   the draft-reset generation makes a mounted editor reseed off `groups`,
   *   so `groups` has to already carry the nulled drafts. Requesting the
   *   reseed before the refetch would reseed the editor from the pre-reset row
   *   — re-displaying, and then re-persisting, exactly what was discarded.
   *   (Round 3, fix #6: awaiting the invalidation turned out to be NECESSARY
   *   but not SUFFICIENT — see below.)
   * - Everything after the PATCH is skipped on a rejection (the `await`
   *   throws), so a failed reset leaves the row untouched: drafts, sender,
   *   `senderTouched` pin, badge and open-editor contents all as they were.
   *
   * ── D12 Codex ROUND-3 fixes #2 and #6 ────────────────────────────────────
   *
   * Fix #2 — the whole body now runs INSIDE `runItemOp`, so it genuinely
   * takes turns with a concurrent reassignment of the same item instead of
   * merely refusing to release the shared barrier early. And because
   * `baseSender` is CAPTURED BY THE CALLER against the play the row was
   * showing (`PlayQueueCard.resetBaseSenderFor`: the play's staged sender,
   * else its role-derived default), the queued op re-validates that context
   * immediately before it writes: if the item moved to another play while we
   * were waiting, `baseSender` describes a play this row is no longer in, and
   * writing it would silently overwrite the destination play's
   * server-seeded sender. Aborting is safe as well as correct — a
   * reassignment's own server-side reset already nulled the drafts, which is
   * the other half of what reset does.
   *
   * Fix #6 — the draft-reset generation is no longer bumped here. `await
   * invalidateQueries` guarantees the query CACHE is current, not that React
   * has re-rendered the editor's parent with the new item; bumping in this
   * tick could re-key the editor's draft and reseed it from the PRE-reset
   * `item` prop it still held. The landed reset now RECORDS the trio it is
   * waiting for and the provider's settle effect (in `EnrollQueueContext.tsx`,
   * keyed on `groups`) bumps once that data has demonstrably reached React.
   * See `requestItemDraftReset`'s doc comment.
   */
  const resetItemToBase = useCallback(
    async (itemId: number, baseSender: string | null) => {
      // Captured SYNCHRONOUSLY, at click time — the play the rep was looking
      // at when they computed `baseSender`.
      const capturedPlay = cachedItem(itemId);
      await runItemOp(itemId, async () => {
        // Re-validated after our turn in the queue (fix #2). An item the
        // cache never knew at either end (`capturedPlay === undefined`) has
        // no captured context to have gone stale, so it proceeds — the same
        // posture `assignPlay` takes for a cache miss.
        const current = cachedItem(itemId);
        if (capturedPlay !== undefined && !samePlay(current, capturedPlay)) return;
        beginItemWriteBarrier(itemId);
        try {
          await discardAutosave(itemId);
          await patchItem(itemId, { subject_draft: null, body_draft: null, sender: baseSender });
          await queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
          clearSenderTouched(stagedIdentity, itemId);
          // Fix #6 — request, don't bump. The trio is expressed in
          // `itemReflectsPatch`'s normalized shape (a nulled draft reads back
          // as `''`), which is exactly what the settle effect compares.
          requestItemDraftReset(itemId, {
            sender: baseSender ?? '',
            subject_draft: '',
            body_draft: '',
          });
        } finally {
          endItemWriteBarrier(itemId);
        }
      });
    },
    [runItemOp, cachedItem, beginItemWriteBarrier, endItemWriteBarrier, discardAutosave, patchItem, queryClient, stagedIdentity],
  );

  return { resetItemToBase };
}
